import React, { useState, useContext } from 'react';
import './ha.css';
import { useNavigate } from 'react-router-dom';
import SimpleBar from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';
import * as XLSX from 'xlsx';
import Modal from 'react-bootstrap/Modal';
import ModalAlert from '../template/ModalAlert';
import fetchApi from '../../api/fetchApi';
import { AuthContext } from '../../context/AuthContext';
import { Permit } from '../../types/types';

interface ModalHa {
    isOpenHa: boolean;
    onClose: () => void;
}


const Ha: React.FC<ModalHa> = ({ isOpenHa, onClose }) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewData, setPreviewData] = useState<Record<string, any>[]>([]);
    const navigate = useNavigate();
    const [modalAlertOpen, setModalAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [alertType, setAlertType] = useState<'success' | 'error'>('success');
    const authContext = useContext(AuthContext);

    const validateData = (data: any[]) => {
        const errores: string[] = [];

        data.forEach((row, index) => {
            if (Object.keys(row).length > COLUMN_HEADERS.length) {
                errores.push(`Fila ${index + 1}: Contiene más columnas de las permitidas.`);
            }
            if (typeof row.horasNormales !== "number") {
                errores.push(`Fila ${index + 1}: "Nom" no es un número.`);
            }
            if (typeof row.horasExtra !== "number") {
                errores.push(`Fila ${index + 1}: "Ext" no es un número.`);
            }
            if (typeof row.horasNocturnas !== "number") {
                errores.push(`Fila ${index + 1}: "Noct" no es un número.`);
            }
            if (typeof row.horasGuardia !== "number") {
                errores.push(`Fila ${index + 1}: "Guar" no es un número.`);
            }
        });

        return errores.length > 0 ? errores : null;
    };

    const COLUMN_HEADERS = [
        "Mes-Año", "Sociedad", "DNI", "Tecnico", "Email", "c.coste.", "p.center.", 
        "cod.proy.", "Contrato", "Nombre PO Corto", "Nombre PO", "Centro de coste", 
        "Categoria", "Norm", "Ext", "Noct", "Guar", "Soc Ext"
    ];
    
    const normalizeHeader = (header: string) => {
        return header
            .normalize("NFD") 
            .replace(/[\u0300-\u036f]/g, "") 
            .replace(" ", "") 
            .replace(".", "")
            .replace("-", "")
            .replace(/\s+/g, "") 
            .trim() 
            .toLowerCase();
    };
    
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const file = event.target.files[0];
            setSelectedFile(file);
    
            const reader = new FileReader();
            reader.onload = (e) => {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
    
                const jsonData: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    
                if (jsonData.length > 0) {
                 
                    const fileHeaders = jsonData[0].map(header => normalizeHeader(String(header)));
                    const expectedHeaders = COLUMN_HEADERS.map(header => normalizeHeader(String(header)));
    
                    if (JSON.stringify(fileHeaders) !== JSON.stringify(expectedHeaders)) {

                        const diferencias = expectedHeaders.filter(h => !fileHeaders.includes(h))
                            .concat(fileHeaders.filter(h => !expectedHeaders.includes(h)));
    
                        setAlertMessage(`El archivo tiene columnas incorrectas o adicionales: ${diferencias.join(", ")}`);
                        setAlertType("error");
                        setModalAlertOpen(true);
    
                        setSelectedFile(null);
                        setPreviewData([]);
    
                        const fileInput = document.getElementById("file-input") as HTMLInputElement;
                        if (fileInput) fileInput.value = "";
    
                        return;
                    }
    
                    jsonData.shift(); 
                    setPreviewData(jsonData);
                } else {
                    setPreviewData([]);
                }
            };
    
            reader.readAsArrayBuffer(file);
        }
    };

    const transformData = (data: any[][]) => {
        return data.map(row => {
            if (!Array.isArray(row) || row.length !== COLUMN_HEADERS.length) return {}; 
            
            return {
                mesAnio: row[0] || "",
                sociedad: row[1] || "",
                dni: row[2] ? String(row[2]) : "", 
                tecnico: row[3] || "",
                email: row[4] || "",
                centroCosto: row[5] || "",
                pCenter: row[6] || "",
                codProveedor: row[7] || "",
                contrato: row[8] || "",
                nombrePOCorto: row[9] || "",
                nombrePO: row[10] || "",
                centroCoste: row[11] || "",
                categoria: row[12] || "",
                horasNormales: row[13] || 0,
                horasExtra: row[14] || 0,
                horasNocturnas: row[15] || 0,
                horasGuardia: row[16] || 0,
                socExt: row[17] || "",
            };
        }).filter(obj => Object.keys(obj).length > 0);
    };

    const handleFileUpload = async () => {
        if (!selectedFile) {
            setAlertMessage("Por favor selecciona un archivo antes de subirlo.");
            setAlertType("error");
            setModalAlertOpen(true);
            return;
        }

        try {
            const formattedData = transformData(previewData as any[][]);

            const errores = validateData(formattedData);
            if (errores) {
                setAlertMessage(`Error en los datos:\n${errores.join("\n")}`);
                setAlertType("error");
                setModalAlertOpen(true);
                return;
            }

            const response = await fetchApi({
                endpoint: "/reporte-ha",
                method: "POST",
                body: formattedData,
            });

            if (response) {
                setAlertMessage("Datos enviados correctamente");
                setAlertType("success");

                setSelectedFile(null);
                setPreviewData([]);

                const fileInput = document.getElementById("file-input") as HTMLInputElement;
                if (fileInput) fileInput.value = "";
            } else {
                setAlertMessage("Error al enviar los datos");
                setAlertType("error");
            }
        } catch (error) {
            console.error("Error:", error);
            setAlertMessage("Error al enviar los datos");
            setAlertType("error");
        }
        setModalAlertOpen(true);
    };

    const hasPermission = (moduleName: string, actionName: string): boolean => {
        return authContext?.user?.permits?.some((permit: Permit) =>
            permit.moduleName === moduleName && permit.actionName === actionName
        ) ?? false;
    };  

    if (!hasPermission("ha", "read")) {
        return <div className="dashboard-container"><p>No tienes permiso para ver esta sección.</p></div>;
    }

    return (
        <Modal show={isOpenHa} onHide={onClose} size="xl" fullscreen="lg-down">
        <Modal.Header closeButton className="modal-header-custom">
            <Modal.Title className="white">Importar Reporte HA</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <div className="ha-card">
                <div className="ha-file-input-container">
                    <input
                        type="file"
                        accept=".xlsx"
                        onChange={handleFileChange}
                        id="file-input"
                        className="ha-file-input"
                    />
                    <label htmlFor="file-input" className="ha-file-label">
                        Seleccionar Archivo
                    </label>
                    <p className="ha-file-info">
                        {selectedFile ? `Archivo seleccionado: ${selectedFile.name}` : 'No has seleccionado ningún archivo'}
                    </p>
                </div>
                <button onClick={handleFileUpload} className="ha-btn" disabled={!selectedFile}>Subir Archivo</button>
            </div>

            {previewData.length > 0 ? (
                <div className="ha-card">
                    <h2 className="ha-subtitle">Previsualización de los Datos</h2>
                    <SimpleBar style={{ maxHeight: '600px' }}>
                        <table className="ha-table">
                            <thead>
                                <tr>
                                    {COLUMN_HEADERS.map((header, index) => (
                                        <th key={index}>{header}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {previewData.map((data, index) => (
                                    <tr key={index}>
                                        {Object.values(data).map((value, i) => (
                                            <td key={i}>{String(value)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </SimpleBar>
                </div>
            ) : (
                <p>No hay datos para mostrar</p>
            )}
            </Modal.Body>
            <ModalAlert
                isOpen={modalAlertOpen}
                onClose={() => setModalAlertOpen(false)}
                message={alertMessage}
                type={alertType}
            />
        </Modal>
    );
};

export default Ha;
